import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { favoriteService, getLocalizedValue } from '@/services/api';
import { useAuth } from './AuthContext';

const FavoritesContext = createContext(null);

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  // Fetch favorites from API when authenticated
  const fetchFavorites = useCallback(async () => {
    if (!isAuthenticated) {
      // Load from localStorage for non-authenticated users
      const stored = localStorage.getItem('favorites');
      if (stored) {
        try {
          setFavorites(JSON.parse(stored));
        } catch (e) {
          console.error('Failed to parse favorites:', e);
        }
      }
      return;
    }

    setLoading(true);
    try {
      const response = await favoriteService.getAll();
      const data = response.data?.data || response.data || [];
      
      // Transform favorites from API to our format
      const transformedFavorites = Array.isArray(data) ? data.map(fav => {
        const doctor = fav.doctor || fav;
        return {
          id: doctor.id,
          name: getLocalizedValue(doctor.name, 'ar') || doctor.name,
          name_ar: getLocalizedValue(doctor.name, 'ar'),
          name_en: getLocalizedValue(doctor.name, 'en'),
          specialty: doctor.specialties?.[0] ? getLocalizedValue(doctor.specialties[0].name, 'ar') : '',
          image: doctor.media?.[0]?.url || doctor.media?.[0]?.thumb,
          rating: doctor.rate || 0,
          reviewCount: doctor.total_reviews || 0,
        };
      }) : [];
      
      setFavorites(transformedFavorites);
      // Also save to localStorage as backup
      localStorage.setItem('favorites', JSON.stringify(transformedFavorites));
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
      // Fallback to localStorage
      const stored = localStorage.getItem('favorites');
      if (stored) {
        try {
          setFavorites(JSON.parse(stored));
        } catch (e) {
          console.error('Failed to parse favorites:', e);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Load favorites on mount and when auth changes
  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('favorites', JSON.stringify(favorites));
  }, [favorites]);

  const addFavorite = async (doctor) => {
    // Optimistically update UI
    setFavorites(prev => {
      if (prev.some(d => d.id === doctor.id)) return prev;
      return [...prev, doctor];
    });

    // Sync with API if authenticated
    if (isAuthenticated) {
      try {
        await favoriteService.add(doctor.id);
      } catch (error) {
        console.error('Failed to add favorite to API:', error);
        // Revert on failure
        setFavorites(prev => prev.filter(d => d.id !== doctor.id));
      }
    }
  };

  const removeFavorite = async (doctorId) => {
    // Store current state for rollback
    const previousFavorites = favorites;
    
    // Optimistically update UI
    setFavorites(prev => prev.filter(d => d.id !== doctorId));

    // Sync with API if authenticated
    if (isAuthenticated) {
      try {
        await favoriteService.remove(doctorId);
      } catch (error) {
        console.error('Failed to remove favorite from API:', error);
        // Revert on failure
        setFavorites(previousFavorites);
      }
    }
  };

  const toggleFavorite = async (doctor) => {
    if (isFavorite(doctor.id)) {
      await removeFavorite(doctor.id);
    } else {
      await addFavorite(doctor);
    }
  };

  const isFavorite = (doctorId) => {
    return favorites.some(d => d.id === doctorId);
  };

  const value = {
    favorites,
    loading,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    favoritesCount: favorites.length,
    refreshFavorites: fetchFavorites,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
